// Every tax invoice the user has been issued, in one place:
//  • Premium discussion comments they paid to unlock.
//  • Premium chat replies they paid to unlock (webhook agents — free trial
//    unlocks are ₹0 and never invoiced).
//  • Hosting subscriptions for hosted (RAG) agents they own.
// Webhook agents have no hosting subscription, so their only invoices are the
// premium-chat ones above.
import { useMemo, useState } from 'react'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { useGetMyUnlockInvoicesQuery, useGetMyCommentUnlockInvoicesQuery } from '../../store/api/chatApi'
import { useGetMySubscriptionInvoicesQuery } from '../../store/api/agentSubscriptionsApi'
import { api } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { Card } from '../../components/ui'
import s from '../../styles/pages/SettingsPage.module.css'

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const fmtRupees = (paise: number) => `₹${(paise / 100).toLocaleString('en-IN')}`

type InvoiceKind = 'chat' | 'comment' | 'subscription'

interface InvoiceRow {
  key: string
  kind: InvoiceKind
  id: string
  invoiceNumber: string
  issuedAt: string
  totalPaise: number
  isPlaceholder: boolean
  agentName: string
}

const KIND_LABEL: Record<InvoiceKind, string> = {
  chat: 'Premium chat',
  comment: 'Premium comment',
  subscription: 'Agent hosting',
}

// includeHosting=false omits hosting-subscription invoices — the /billing page
// already lists those per agent, so it only wants the reader-side purchases.
export function SettingsBilling({ includeHosting = true }: { includeHosting?: boolean } = {}) {
  const { accessToken } = useAppSelector((st) => st.auth)
  const { data: chatData, isLoading: chatLoading } = useGetMyUnlockInvoicesQuery()
  const { data: commentData, isLoading: commentLoading } = useGetMyCommentUnlockInvoicesQuery()
  const { data: subData, isLoading: subLoading } = useGetMySubscriptionInvoicesQuery(undefined, { skip: !includeHosting })
  const [openingKey, setOpeningKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const invoices = useMemo<InvoiceRow[]>(() => {
    const chat = (chatData?.invoices ?? []).map(inv => ({ ...inv, key: `chat:${inv.id}`, kind: 'chat' as const }))
    const comment = (commentData?.invoices ?? []).map(inv => ({ ...inv, key: `comment:${inv.id}`, kind: 'comment' as const }))
    const sub = (includeHosting ? (subData?.invoices ?? []) : []).map(inv => ({ ...inv, key: `subscription:${inv.id}`, kind: 'subscription' as const }))
    return [...chat, ...comment, ...sub].sort((a, b) => new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime())
  }, [chatData, commentData, subData, includeHosting])
  const isLoading = chatLoading || commentLoading || subLoading

  const handleOpen = async (inv: InvoiceRow) => {
    // Open the tab synchronously so the browser doesn't block it after the await.
    const win = window.open('', '_blank')
    setOpeningKey(inv.key); setError(null)
    try {
      const [service, path] = inv.kind === 'chat'
        ? ['ai', ENDPOINTS.chat.unlockInvoiceDownload(inv.id)] as const
        : inv.kind === 'comment'
          ? ['comments', ENDPOINTS.comments.unlockInvoiceDownload(inv.id)] as const
          : ['ai', ENDPOINTS.agentSubscriptions.myInvoiceDownload(inv.id)] as const
      const { blob, filename } = await api.downloadFile(service, path, accessToken)
      const url = URL.createObjectURL(blob)
      if (win) {
        win.location.href = url
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = filename ?? 'invoice.pdf'
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch {
      win?.close()
      setError('Could not open that invoice. Please try again shortly.')
    } finally {
      setOpeningKey(null)
    }
  }

  return (
    <Card>
      <div className={s.accountTitle}>Invoices</div>
      <p style={{ margin: '0 0 0.9rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
        A GST tax invoice is generated each time you pay — to unlock a premium chat reply or discussion comment, or to subscribe an agent you host.
      </p>
      {error && <div style={{ fontSize: '0.75rem', color: 'var(--error)', marginBottom: '0.5rem' }}>{error}</div>}
      {isLoading && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Loading…</div>}
      {!isLoading && invoices.length === 0 && (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          No invoices yet — one appears here after you make a payment.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {invoices.map(inv => (
          <div key={inv.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.8rem', padding: '0.55rem 0.8rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: 'monospace', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.invoiceNumber}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                {KIND_LABEL[inv.kind]} · {inv.agentName} · {fmtDate(inv.issuedAt)} · {fmtRupees(inv.totalPaise)}{inv.isPlaceholder ? ' · SAMPLE' : ''}
              </div>
            </div>
            <button
              onClick={() => void handleOpen(inv)}
              disabled={openingKey === inv.key}
              style={{ fontSize: '0.72rem', flexShrink: 0, background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '0.3rem 0.6rem', color: 'var(--text-primary)', cursor: 'pointer' }}
            >
              {openingKey === inv.key ? 'Opening…' : '👁 View / Download'}
            </button>
          </div>
        ))}
      </div>
    </Card>
  )
}
